// BoardViewCardFields.jsx

import React from 'react';
import { isEnumCompatibleFieldType } from 'utilities/FieldTypes';

// Utilities
import { getEnumOptionColor, getEnumOptionIconKey, getEnumOptionLabel } from 'utilities/EnumOptions';
import { getPeopleProfiles } from 'views/utils/peopleFieldProfiles';

// CSS
import './BoardViewCardFields.css';

// Components
import Pill from 'components/pill/Pill';
import ProfilePhoto from 'components/profilephoto/ProfilePhoto';

/**
 * BoardViewCardFields
 *
 * @returns {ReactNode} The rendered component.
 */
const BoardViewCardFields = ({
    node,
    cardFieldKeys,
    schemaFieldMap,
    schemaFieldMapLower,
    resolveFieldValue,
    formatFieldValue,
    language,
    theme,
}) => {

    if (!cardFieldKeys.length) return null;

    // ----- RENDER ----- //

    return (
        <div className="board-view-card-fields">
            {cardFieldKeys.map((fieldKey) => {
                const normalizedFieldKey = String(fieldKey || '').trim();
                const normalizedFieldKeyLower = normalizedFieldKey.toLowerCase();
                const fieldDef = schemaFieldMap.get(normalizedFieldKey)
                    || schemaFieldMapLower.get(normalizedFieldKeyLower)
                    || null;
                const inferredFieldType = normalizedFieldKeyLower === 'tags'
                    ? 'tags'
                    : (normalizedFieldKeyLower === 'datecreated' || normalizedFieldKeyLower === 'datemodified'
                        ? 'date'
                        : '');
                const fieldType = String(fieldDef?.type || inferredFieldType).toLowerCase();
                const fallbackLabel = normalizedFieldKeyLower === 'datecreated'
                    ? 'Created'
                    : (normalizedFieldKeyLower === 'datemodified' ? 'Modified' : fieldKey);
                const label = normalizedFieldKeyLower === 'autokey'
                    ? 'KEY'
                    : (
                        fieldDef?.label
                        || fieldDef?.name
                        || fallbackLabel
                    );
                const resolvedValue = fieldDef
                    ? resolveFieldValue(node, fieldDef)
                    : (node?.[normalizedFieldKey] ?? node?.[fieldKey]);
                const rawValue = (resolvedValue === undefined && fieldType === 'tags')
                    ? node?.tags
                    : resolvedValue;

                if (isEnumCompatibleFieldType(fieldType)) {
                    const enumValue = rawValue == null ? '' : String(rawValue).trim();
                    if (!enumValue) return null;
                    const enumLabel = getEnumOptionLabel(fieldDef?.options || [], enumValue, language);
                    if (!enumLabel) return null;
                    const enumColor = getEnumOptionColor(fieldDef?.options || [], enumValue);
                    const enumIconKey = getEnumOptionIconKey(fieldDef?.options || [], enumValue);

                    return (
                        <div
                          className="board-view-card-field"
                          key={`board_card_${node?.key}_${fieldKey}`}
                        >

                            {/* CARD FIELD LABEL */}
                            <span
                              className="board-view-card-field-label"
                            >
                                {label}
                            </span>

                            {/* CARD FIELD PILLS */}
                            <div className="board-view-card-field-pills">
                                <Pill
                                  label={enumLabel}
                                  color={enumColor || theme.panel}
                                  iconKey={enumIconKey}
                                  compact
                                />
                            </div>
                        </div>
                    );
                }

                if (fieldType === 'tags') {
                    const tagValues = (Array.isArray(rawValue) ? rawValue : [rawValue])
                        .map((value) => {
                            if (value == null) return '';
                            if (typeof value === 'object') {
                                const candidate = value?.label || value?.name || value?.value;
                                return String(candidate || '').trim();
                            }
                            return String(value).trim();
                        })
                        .filter(Boolean);

                    if (!tagValues.length) return null;

                    return (
                        <div
                          className="board-view-card-field"
                          key={`board_card_${node?.key}_${fieldKey}`}
                        >

                            {/* CARD FIELD LABEL */}
                            <span
                              className="board-view-card-field-label"
                            >
                                {label}
                            </span>

                            {/* CARD FIELD PILLS */}
                            <div className="board-view-card-field-pills">
                                {tagValues.map((tag, tagIndex) => (
                                    <Pill
                                      key={`board_card_${node?.key}_${fieldKey}_tag_${tag}_${tagIndex}`}
                                      label={tag}
                                      compact
                                    />
                                ))}
                            </div>
                        </div>
                    );
                }

                if (fieldType === 'people') {
                    const peopleProfiles = getPeopleProfiles(rawValue);
                    if (!peopleProfiles.length) return null;

                    return (
                        <div
                          className="board-view-card-field"
                          key={`board_card_${node?.key}_${fieldKey}`}
                        >

                            {/* CARD FIELD LABEL */}
                            <span
                              className="board-view-card-field-label"
                            >
                                {label}
                            </span>

                            {/* CARD FIELD PEOPLE */}
                            <div className="board-view-card-field-people">
                                {peopleProfiles.map((person) => (
                                    <div
                                      className="board-view-card-person"
                                      key={`board_card_${node?.key}_${fieldKey}_${person.key}`}
                                    >
                                        <ProfilePhoto
                                          node={{
                                                key: person.key,
                                                name: person.name,
                                                title: person.name,
                                                coverUrl: person.coverUrl || null,
                                            }}
                                          size={20}
                                          borderWidth={1}
                                        />

                                        {/* CARD PERSON NAME */}
                                        <span
                                          className="board-view-card-person-name"
                                        >
                                            {person.name}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                }

                const displayValue = formatFieldValue(rawValue, fieldDef, fieldKey);
                if (!displayValue) return null;
                return (
                    <div
                      className="board-view-card-field"
                      key={`board_card_${node?.key}_${fieldKey}`}
                    >

                        {/* CARD FIELD LABEL */}
                        <span
                          className="board-view-card-field-label"
                        >
                            {label}
                        </span>

                        {/* CARD FIELD VALUE */}
                        <span
                          className="board-view-card-field-value"
                        >
                            {displayValue}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

// Dev Tools
BoardViewCardFields.displayName = 'BoardViewCardFields';

export default React.memo(BoardViewCardFields);
